import { Controller } from "@hotwired/stimulus"

// Manages the claim to-do list: HTML5 drag-and-drop reordering of active
// items (persisted via PATCH to reorderUrl), plus light hooks for the
// complete / uncomplete / delete buttons. Those buttons are `button_to`
// forms wired with `data-turbo-stream`, so Turbo owns the actual network
// request and DOM replacement — the matching actions here only provide
// optimistic UI feedback so they never double-submit.
export default class extends Controller {
  static targets = ["list", "item"]
  static values = { reorderUrl: String }

  connect() {
    this.draggingItem = null
  }

  // --- Drag and drop reordering -------------------------------------------

  dragstart(event) {
    this.draggingItem = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    // Some browsers require data to be set for the drag to initiate.
    if (this.draggingItem.dataset.todoId) {
      event.dataTransfer.setData("text/plain", this.draggingItem.dataset.todoId)
    }
    // Defer so the drag image is captured before the element fades.
    requestAnimationFrame(() => {
      this.draggingItem.classList.add("opacity-50")
    })
  }

  dragend() {
    if (this.draggingItem) {
      this.draggingItem.classList.remove("opacity-50")
    }
    this.draggingItem = null
    this.clearDragOver()
  }

  dragover(event) {
    if (!this.draggingItem) return

    const target = event.currentTarget
    // Only reorder within the active list and not onto the dragged item itself.
    if (target === this.draggingItem || !this.isInList(target)) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const list = this.listTarget
    const rect = target.getBoundingClientRect()
    const after = event.clientY > rect.top + rect.height / 2

    if (after) {
      list.insertBefore(this.draggingItem, target.nextSibling)
    } else {
      list.insertBefore(this.draggingItem, target)
    }
  }

  dragleave() {
    // No visual drop-zone state to clear per-item; reordering happens live in
    // dragover. Kept as a declared action hook for the view wiring.
  }

  drop(event) {
    if (!this.draggingItem) return
    event.preventDefault()
    this.persistOrder()
  }

  // --- Completion / deletion hooks ----------------------------------------
  // Turbo submits the underlying button_to form (data-turbo-stream) and
  // swaps the rendered item, so these stay no-op-light to avoid duplicate
  // requests. They exist because the view declares the actions.

  completeItem() {
    // Turbo Stream handles the toggle + re-render.
  }

  uncompleteItem() {
    // Turbo Stream handles the toggle + re-render.
  }

  delete() {
    // Turbo Stream removes the item; nothing extra needed here.
  }

  // --- Helpers ------------------------------------------------------------

  isInList(element) {
    return this.hasListTarget && this.listTarget.contains(element)
  }

  clearDragOver() {
    if (!this.hasItemTarget) return
    this.itemTargets.forEach((item) => item.classList.remove("opacity-50"))
  }

  persistOrder() {
    if (!this.hasReorderUrlValue || !this.hasListTarget) return

    const positions = {}
    this.activeItems().forEach((item, index) => {
      const id = item.dataset.todoId
      if (id) positions[id] = index + 1
    })

    if (Object.keys(positions).length === 0) return

    fetch(this.reorderUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html, application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({ positions })
    }).catch(() => {
      // Reordering is best-effort; the next page load reflects server truth.
    })
  }

  activeItems() {
    // Only items inside the active list target should be persisted, even
    // though completed items in the collapsed section also carry the target.
    return this.itemTargets.filter((item) => this.listTarget.contains(item))
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
